/**
 * Dev-only mock data interceptor for local UI previewing without a backend.
 *
 * Activated by: localStorage.setItem('dev_mock_mode', 'true')
 * Mock data from: localStorage.getItem('dev_mock_api') → JSON keyed by URL path
 *
 * Safety: This is a complete no-op in production — the interceptor is only
 * registered when the dev_mock_mode flag exists (set by scripts/dev-frontend.sh).
 * It only intercepts network errors (backend unreachable), never real HTTP responses.
 */
import api from "./api";

/** Generic paginated list filter: search fields + equality filters + pagination. */
function applyListFilters(
  data: { items: Record<string, unknown>[]; total: number; limit?: number; offset?: number },
  params: Record<string, unknown> | undefined,
  opts?: {
    searchFields?: string[];
    equalityFields?: string[];
    sortField?: string;
    dateField?: string;
  },
) {
  if (!params) return data;
  let items = [...data.items];
  const searchFields = opts?.searchFields || ["name", "description"];
  const equalityFields = opts?.equalityFields || [];

  // Free-text search across configured fields
  const search = (params.search as string || "").toLowerCase();
  if (search) {
    items = items.filter((item) =>
      searchFields.some((f) => String(item[f] || "").toLowerCase().includes(search)),
    );
  }

  // Author search (newsletter-specific: searches author_name and author_email)
  const author = (params.author as string || "").toLowerCase();
  if (author) {
    items = items.filter((item) =>
      String(item.author_name || "").toLowerCase().includes(author) ||
      String(item.author_email || "").toLowerCase().includes(author),
    );
  }

  // Equality filters
  for (const field of equalityFields) {
    if (params[field] !== undefined && params[field] !== null && params[field] !== "") {
      if (typeof params[field] === "boolean" || params[field] === "true" || params[field] === "false") {
        const boolVal = String(params[field]) === "true";
        items = items.filter((item) => item[field] === boolVal);
      } else {
        items = items.filter((item) => item[field] === params[field]);
      }
    }
  }

  // Team-specific extra filters (jira_project_key, timezone, etc.)
  if (params.jira_project_key) {
    const key = String(params.jira_project_key).toUpperCase();
    items = items.filter((t) => String(t.jira_project_key || "").toUpperCase().includes(key));
  }
  if (params.timezone) {
    const tz = String(params.timezone).toLowerCase();
    items = items.filter((t) => String(t.timezone || "").toLowerCase().includes(tz));
  }

  // Date range filters (from_date / to_date on configurable dateField, or created_after/before on created_at)
  const dateField = opts?.dateField || "created_at";
  if (params.from_date) {
    items = items.filter((t) => String(t[dateField] || "") >= String(params.from_date));
  }
  if (params.to_date) {
    items = items.filter((t) => String(t[dateField] || "") <= String(params.to_date));
  }
  if (params.created_after) {
    items = items.filter((t) => String(t.created_at || "") >= String(params.created_after));
  }
  if (params.created_before) {
    items = items.filter((t) => String(t.created_at || "") <= String(params.created_before));
  }

  // Sorting
  const sortBy = String(params.sort_by || opts?.sortField || "created_at");
  const sortOrder = String(params.sort_order || "desc");
  items.sort((a, b) => {
    const aVal = String(a[sortBy] || "");
    const bVal = String(b[sortBy] || "");
    const cmp = aVal.localeCompare(bVal);
    return sortOrder === "asc" ? cmp : -cmp;
  });

  const total = items.length;
  const offset = Number(params.offset) || 0;
  const limit = Number(params.limit) || Number(params.page_size) || 100;
  const sliced = items.slice(offset, offset + limit);

  return { items: sliced, total, limit, offset };
}

/** Route config: URL → filter options. */
const FILTERABLE_ROUTES: Record<string, { searchFields: string[]; equalityFields: string[]; dateField?: string }> = {
  "/teams": { searchFields: ["name", "description"], equalityFields: ["approval_status", "is_active"] },
  "/admin/teams": { searchFields: ["name", "google_space_id"], equalityFields: ["approval_status", "is_active"] },
  "/admin/users": { searchFields: ["email", "display_name"], equalityFields: ["is_active", "is_admin", "is_super_admin"] },
  "/admin/invitation-codes": { searchFields: [], equalityFields: ["is_used"] },
  "/feedback": { searchFields: ["title", "description"], equalityFields: ["status", "type", "priority"] },
  "/admin/newsletter/topics": { searchFields: ["confirmed_message", "original_message", "author_name", "author_email"], equalityFields: ["category"], dateField: "date" },
  "/admin/groups": { searchFields: ["name", "description"], equalityFields: [] },
};

// ---------------------------------------------------------------------------
// Mock responses for team-level Google OAuth endpoints (dynamic :teamId paths).
// Kept at module scope so the helper function satisfies no-inner-declarations.
// ---------------------------------------------------------------------------
const GOOGLE_OAUTH_CONFIG_MOCK = {
  has_token: true,
  authorized_email: "teamlead@example.com",
  authorized_at: "2026-03-15T10:00:00Z",
  is_valid: true,
};
const GOOGLE_OAUTH_INIT_MOCK = {
  authorization_url: "https://accounts.google.com/o/oauth2/auth?mock=true",
  message: "Mock OAuth flow",
};
const GOOGLE_OAUTH_CLEAR_MOCK = { message: "Google OAuth configuration cleared" };
const GOOGLE_OAUTH_TEST_MOCK = { status: "success", message: "Connection is valid" };

/** Returns mock data for dynamic team Google OAuth URLs, or null if not matched. */
function matchTeamGoogleOAuth(
  url: string,
  method: string,
): Record<string, unknown> | null {
  const m = (method || "GET").toUpperCase();
  // /teams/<id>/google-oauth-config  (GET = status, DELETE = clear)
  if (/^\/teams\/[^/]+\/google-oauth-config$/.test(url)) {
    if (m === "GET") return GOOGLE_OAUTH_CONFIG_MOCK;
    if (m === "DELETE") return GOOGLE_OAUTH_CLEAR_MOCK;
  }
  // /teams/<id>/google-oauth-config/init  (POST = start OAuth flow)
  if (/^\/teams\/[^/]+\/google-oauth-config\/init$/.test(url) && m === "POST") {
    return GOOGLE_OAUTH_INIT_MOCK;
  }
  // /teams/<id>/google-oauth-config/test  (GET = test connection)
  if (/^\/teams\/[^/]+\/google-oauth-config\/test$/.test(url) && m === "GET") {
    return GOOGLE_OAUTH_TEST_MOCK;
  }
  return null;
}

if (typeof window !== "undefined") {
  try {
    if (localStorage.getItem("dev_mock_mode") === "true") {
      let mockData: Record<string, unknown> = {};
      try {
        const raw = localStorage.getItem("dev_mock_api");
        if (raw) mockData = JSON.parse(raw);
      } catch {
        // invalid JSON in localStorage, skip
      }

      api.interceptors.response.use(
        (response) => response,
        (error) => {
          // Only intercept network errors (no response = backend is down).
          // Real HTTP errors (401, 404, etc.) pass through untouched.
          if (error.response) return Promise.reject(error);

          const url = (error.config?.url || "").split("?")[0];
          const method = error.config?.method || "get";

          // Check dynamic Google OAuth patterns first
          const dynamicMock = matchTeamGoogleOAuth(url, method);
          if (dynamicMock !== null) {
            return Promise.resolve({
              data: dynamicMock,
              status: 200,
              statusText: "OK (mock)",
              headers: {},
              config: error.config,
            });
          }

          if (url in mockData) {
            let responseData = mockData[url];

            // Apply filtering + pagination for list endpoints
            const filterOpts = FILTERABLE_ROUTES[url];
            if (filterOpts && responseData && typeof responseData === "object" && "items" in (responseData as Record<string, unknown>)) {
              responseData = applyListFilters(
                responseData as { items: Record<string, unknown>[]; total: number; limit: number; offset: number },
                error.config?.params,
                filterOpts,
              );
            }

            // Onboarding pending: filter by status param
            if (url === "/admin/onboarding/pending" && responseData && error.config?.params?.status) {
              const rd = responseData as { requests: Record<string, unknown>[]; [k: string]: unknown };
              const filtered = rd.requests.filter((r) => r.status === error.config.params.status);
              responseData = { ...rd, requests: filtered };
            }

            return Promise.resolve({
              data: responseData,
              status: 200,
              statusText: "OK (mock)",
              headers: {},
              config: error.config,
            });
          }

          return Promise.reject(error);
        },
      );
    }
  } catch {
    // localStorage not available (SSR, etc.), skip silently
  }
}
